import Component from '@glimmer/component'
import { service } from '@ember/service'
import { format } from 'date-fns'
import type { IntlService } from 'ember-intl'
import BasicTable from 'teacher-ui-client/components/stable-table/basic-table'
import CalendarIconCell from 'teacher-ui-client/components/stable-table/basic-cells/calendar-icon'
import { NoDataMessage } from 'teacher-ui-client/components/no-data-message'
import { gte, notEq } from 'ember-truth-helpers'
import type { ThemeKeys } from 'teacher-ui-client/utils/themes'
import type { SafeString } from '@ember/template'

/**
 * Provide this component with rows of dates, labels and values to make an activity type table
 *
 * headerLabel: 'Recent Activity'
 * subHeaderLabel: 'Score'
 * tableData: [
 *  { date: new Date(), label: 'Peak 3', value: 1 },
 *  { date: new Date(), label: 'Peak 4', value: 9 },
 * ]
 *
 * Renders a table like this;
 *
 * Recent Activity_______________Score
 * Mar 25 | Peak 3              |  1
 * May 17 | Peak 4              |  9
 */

interface Args {
  theme?: ThemeKeys
  headerLabel?: string
  subheaderLabel?: string
  tableData: TableDataItem[]
}

export interface TableDataItem {
  date: Date
  label?: string
  value?: number | string | SafeString
}

interface Signature {
  Args: Args
  Element: HTMLTableElement
}

interface Theme {
  border: string
  calendar: string
}

export class StudentDashboardActivityTable extends Component<Signature> {
  @service intl!: IntlService

  get selectedTheme() {
    const themes: Partial<Record<ThemeKeys, Theme>> = {
      blue: { border: 'border-oceany-blue-150', calendar: 'text-oceany-blue-300' },
      green: { border: 'border-ms-green-150', calendar: 'text-ms-green-300' },
      default: { border: 'border-black', calendar: 'text-dusty-black-300' },
    }
    const theme = this.args.theme || 'default'
    return themes[theme]
  }

  get columns() {
    const { headerLabel, subheaderLabel } = this.args
    const borderClass = this.selectedTheme?.border
    const calendarClass = this.selectedTheme?.calendar

    const defaultColumn = {
      classNames: `table-box-header border-b-4 ${borderClass} print:border-black`,
      cellClassNames: `table-box-cell border-b ${borderClass} print:border-black`,
    }

    const columns = [
      {
        ...defaultColumn,
        value: headerLabel,
        span: 2, // this header cell spans two cells
        cellComponent: CalendarIconCell,
        cellClassNames: `${defaultColumn.cellClassNames} ${calendarClass} text-center w-16`,
      },
      {
        ...defaultColumn,
        span: 0, // this header cell will not be rendered. This object is just here to decorate the cells in this column
      },
      {
        ...defaultColumn,
        value: subheaderLabel,
        classNames: `${defaultColumn.classNames} text-center w-32`,
        cellClassNames: `${defaultColumn.cellClassNames} text-center`,
      },
    ]
    return columns
  }

  get rows() {
    const rows = this.args.tableData.map((dataRow) => {
      const { date, label, value } = dataRow

      const monthFormat = this.intl.t('dateFormats.dateFns.MMM')
      const dayFormat = this.intl.t('dateFormats.dateFns.dd')
      const exportFormat = this.intl.t('dateFormats.dateFns.MMM-dd')

      const dateCell = {
        value: {
          month: format(date, monthFormat),
          day: format(date, dayFormat),
          exportValue: format(date, exportFormat),
        },
      }

      const labelCell = { value: label }
      const dataCell = { value }

      const baseRow = { cells: [dateCell, labelCell, dataCell] }

      return baseRow
    })

    return rows
  }

  <template>
    <BasicTable class="w-full" @columns={{this.columns}} @rows={{this.rows}} ...attributes as |bt|>
      {{! A slightly custom header that allows for a way to define colspan, or not render if colspan is zero }}
      <bt.head as |h|>
        <h.row as |r cellMeta|>
          {{#let cellMeta.columnObject.span as |colspan|}}
            {{#if (gte colspan 2)}}
              <r.cell colspan={{colspan}} />
            {{else if (notEq colspan 0)}}
              <r.cell />
            {{/if}}
          {{/let}}
        </h.row>
      </bt.head>

      {{#if this.rows}}
        <bt.body />
      {{else}}
        <bt.spanner>
          <NoDataMessage @compact={{true}} />
        </bt.spanner>
      {{/if}}
    </BasicTable>
  </template>
}

export default StudentDashboardActivityTable
